import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  ClipboardCheck,
  Users,
  ArrowRight,
  CheckCircle2,
  Building2,
  UserPlus,
  UploadCloud,
  Sparkles,
} from "lucide-react";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "£49",
    interval: "per month",
    seats: "Up to 25 workers",
    highlight: false,
    features: [
      "17 standard compliance items",
      "Role-specific checklists (HCA, senior care, chef)",
      "DBS + audit log",
      "Email notifications & reminders",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "£149",
    interval: "per month",
    seats: "Up to 100 workers",
    highlight: true,
    features: [
      "Everything in Starter",
      "NMC monthly-check workflow for nurses",
      "Custom checklist items per organisation",
      "Branded workspace (logo & colour)",
      "Priority support",
    ],
  },
  {
    id: "group",
    name: "Group",
    price: "£399",
    interval: "per month",
    seats: "Up to 500 workers",
    highlight: false,
    features: [
      "Everything in Pro",
      "Multi-home visibility for care groups",
      "Custom sender domain (Resend)",
      "Priority onboarding call",
      "SLA & procurement pack on request",
    ],
  },
];

const STEPS = [
  {
    icon: Building2,
    title: "Create your workspace",
    desc: "Sign up in 60 seconds. You become the first manager and pick a workspace name and colour.",
  },
  {
    icon: UserPlus,
    title: "Invite your team & staff",
    desc: "Add reviewers, other managers, and care workers with a single click. Emails are branded to your workspace.",
  },
  {
    icon: UploadCloud,
    title: "Workers upload, reviewers approve",
    desc: "Each worker sees a personalised checklist. Documents auto-track expiry. You get a live compliance grid ready for inspection.",
  },
];

export default function Landing() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-stone-50 text-slate-900">
      {/* --- Header --- */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center" data-testid="landing-logo">
            <img
              src="/audentra-logo.png"
              alt="Audentra"
              className="h-10 md:h-11 w-auto"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => nav("/login")}
              data-testid="landing-login-btn"
              className="text-slate-700 hover:text-slate-900"
            >
              Sign in
            </Button>
            <Button
              onClick={() => nav("/signup")}
              data-testid="landing-signup-org-btn"
              className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-5"
            >
              Sign up your organisation <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* --- Hero --- */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold border border-teal-100">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-600" /> CQC-ready onboarding
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-6 leading-[1.05]">
            Onboard your care staff{" "}
            <span className="text-teal-700">without the paperwork chaos.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
            A purpose-built compliance workflow for care providers and recruiters.
            Workers upload role-specific documents, your team reviews in one queue,
            and managers see the entire workforce at a glance — ready for CQC inspection.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
            <div className="rounded-xl border-2 border-teal-700 bg-white p-4 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700">
                  <Sparkles className="h-3.5 w-3.5" /> For care operators
                </div>
                <div className="font-heading text-lg font-bold mt-2">Sign up your organisation</div>
                <div className="text-xs text-slate-500 mt-1">14-day trial · no card required</div>
              </div>
              <Button
                onClick={() => nav("/signup")}
                data-testid="hero-signup-org-btn"
                className="mt-4 bg-teal-700 hover:bg-teal-800 text-white rounded-full h-11"
              >
                Get started <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <Users className="h-3.5 w-3.5" /> For care workers
                </div>
                <div className="font-heading text-lg font-bold mt-2">I&apos;m a care worker</div>
                <div className="text-xs text-slate-500 mt-1">Ask your manager for the workspace code</div>
              </div>
              <Button
                variant="outline"
                onClick={() => nav("/register")}
                data-testid="hero-register-btn"
                className="mt-4 rounded-full h-11 border-slate-300"
              >
                Register as a worker
              </Button>
            </div>
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <img
                alt="Care professional"
                src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsJTIwc21pbGluZ3xlbnwwfHx8fDE3ODUzMTYxMzB8MA&ixlib=rb-4.1.0&q=85"
                className="w-full h-[520px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl p-4 shadow-sm hidden md:flex items-center gap-3 w-72">
              <div className="h-10 w-10 rounded-md bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">DBS Certificate</div>
                <div className="text-xs text-emerald-700 font-medium">Approved · 2h ago</div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-white border border-slate-200 rounded-xl p-3 shadow-sm hidden md:flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-amber-50 border border-amber-100 flex items-center justify-center">
                <ClipboardCheck className="h-4 w-4 text-amber-600" />
              </div>
              <div className="text-xs">
                <div className="font-semibold text-slate-900">14 in review</div>
                <div className="text-slate-500">Nursing intake</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- How it works --- */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="text-center">
            <div className="label-caps text-teal-700">How it works</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-3">
              From signed-up to inspection-ready in an afternoon.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="how-it-works">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl border border-slate-200 bg-white p-6">
                <div className="absolute -top-3 left-6 px-2 py-0.5 rounded-full bg-teal-700 text-white text-[10px] font-bold uppercase tracking-widest">
                  Step {i + 1}
                </div>
                <div className="h-10 w-10 rounded-md bg-teal-50 border border-teal-100 flex items-center justify-center mt-2">
                  <s.icon className="h-5 w-5 text-teal-700" />
                </div>
                <div className="font-heading text-lg font-bold mt-4">{s.title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Pricing --- */}
      <section className="bg-stone-50" id="pricing">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="label-caps text-teal-700">Pricing</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-3">
              One transparent monthly price per workspace.
            </h2>
            <p className="text-slate-600 mt-3">
              No per-worker fees inside your seat limit. Cancel or change plan any time from
              the Stripe customer portal.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3" data-testid="landing-plans">
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={`landing-plan-${p.id}`}
                className={`rounded-2xl border-2 bg-white p-6 flex flex-col ${
                  p.highlight ? "border-teal-700 ring-2 ring-teal-100 shadow-md" : "border-slate-200"
                }`}
              >
                {p.highlight && (
                  <div className="self-start mb-2 inline-flex items-center gap-1 rounded-full bg-teal-700 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                    <Sparkles className="h-3 w-3" /> Most popular
                  </div>
                )}
                <div className="font-heading text-xl font-bold">{p.name}</div>
                <div className="text-xs text-slate-500 mt-1">{p.seats}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <div className="font-heading text-4xl font-bold">{p.price}</div>
                  <div className="text-slate-500 text-sm">{p.interval}</div>
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => nav("/signup")}
                  data-testid={`landing-plan-cta-${p.id}`}
                  className={`mt-6 rounded-full h-11 ${
                    p.highlight
                      ? "bg-teal-700 hover:bg-teal-800 text-white"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  Start 14-day free trial <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-slate-500">
            Every plan starts with a free 14-day trial — no card required. Payments processed securely by Stripe.
          </p>
        </div>
      </section>

      {/* --- Roles strip --- */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="grid lg:grid-cols-3 gap-10">
            <div>
              <div className="label-caps text-slate-400">Everyone lands somewhere useful</div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mt-3 leading-tight">
                Built around the three people who move a hire forward.
              </h2>
              <p className="text-slate-300 mt-4">
                One workflow — three surfaces. Everyone knows exactly what&apos;s expected of them.
              </p>
            </div>
            <div className="lg:col-span-2 grid md:grid-cols-3 gap-4">
              {[
                {
                  icon: Users,
                  title: "Workers",
                  desc: "Self-register, pick a role, and complete a personalised checklist. Re-upload after rejection with clear notes.",
                },
                {
                  icon: ClipboardCheck,
                  title: "Team Members",
                  desc: "Review the pending queue, approve or reject with a reason, and upload on behalf of workers when needed.",
                },
                {
                  icon: ShieldCheck,
                  title: "Managers",
                  desc: "See every worker's status in one grid. Invite team members, escalate rejections, and lock in compliance.",
                },
              ].map((c) => (
                <div key={c.title} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
                  <c.icon className="h-6 w-6 text-teal-400" />
                  <div className="font-heading text-lg font-semibold mt-4">{c.title}</div>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{c.desc.replace(/'/g, "\u2019")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- Final CTA --- */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-16 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            Ready to get inspection-ready?
          </h2>
          <p className="text-slate-600 mt-3">
            Set up your Audentra workspace in under a minute. No card, no contract, no commitment.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Button
              size="lg"
              onClick={() => nav("/signup")}
              data-testid="final-signup-org-btn"
              className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-7 h-12"
            >
              Sign up your organisation <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => nav("/login")}
              data-testid="final-signin-btn"
              className="rounded-full px-7 h-12 border-slate-300"
            >
              I already have an account
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-6 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <div>© {new Date().getFullYear()} Audentra Compliance</div>
          <div>Confidential · Only cleared reviewers see documents.</div>
        </div>
      </footer>
    </div>
  );
}
